/**
 * Pixi character renderer.
 *
 * This mirrors the web avatar data model, but draws with Pixi primitives
 * so games can show actual player identity without pulling React/SVG into
 * game bundles.
 */
import type { CharacterIcon } from '@tt/types';
import { Container, Graphics, Sprite, Text, TextStyle, type Texture } from 'pixi.js';

// ── Pre-rendered icon-SVG texture registry ──────────────────────────────
//
// The web bundle pre-rasterizes each match player's full SVG IconAvatar
// (the same renderer the lobby uses) at game-boot time and registers the
// resulting Pixi Texture here under a hash of the icon's fields. Any
// future drawCharacter / CharacterSprite call with that same icon picks
// up the texture and renders a pixel-identical Sprite — instead of
// falling back to the Pixi primitive shapes.
//
// Storage is on globalThis so it's *bulletproof against module
// duplication* — when Vite emits separate copies of this module into
// dynamically-imported game chunks, every copy reads from / writes to the
// same Map. (Subtle real bug: without this, GameStage would register a
// texture in the web-main copy's Map, and the dynamically-loaded game's
// drawCharacter would look up an empty Map in its own duplicate copy and
// fall through to the primitive renderer — exactly the symptom of
// in-game characters not matching the lobby.)
interface TTGlobal {
  __ttCharacterTextures?: Map<string, Texture>;
  __ttCharacterSvgs?: Map<string, string>;
}
function registry(): Map<string, Texture> {
  const g = globalThis as TTGlobal;
  if (!g.__ttCharacterTextures) g.__ttCharacterTextures = new Map<string, Texture>();
  return g.__ttCharacterTextures;
}
function svgRegistry(): Map<string, string> {
  const g = globalThis as TTGlobal;
  if (!g.__ttCharacterSvgs) g.__ttCharacterSvgs = new Map<string, string>();
  return g.__ttCharacterSvgs;
}

/** Stable key for an icon's full appearance. Changing any cosmetic
 *  field produces a new hash, so multiple variants of the same charId
 *  can coexist (rare, but defensive). */
export function iconHash(icon: CharacterIcon | undefined): string {
  if (!icon) return 'none';
  return [
    icon.set, icon.id, icon.hue,
    icon.body ?? -1, icon.hat ?? -1, icon.face ?? -1,
    icon.accentHue ?? -1, icon.paletteId ?? '',
    icon.border ?? '', icon.background ?? '', icon.mask ?? '', icon.outfit ?? '',
    icon.particles ?? '', icon.rimHue ?? -1,
  ].join('|');
}

/** Web-bundle-only entry point: register a pre-rendered SVG texture for
 *  an icon hash. drawCharacter / CharacterSprite will use it. */
export function setCharacterTextureByIconHash(hash: string, texture: Texture): void {
  registry().set(hash, texture);
}

/** Web-bundle entry point: register the lobby's IconAvatar SVG fragment
 *  (a full `<svg>…</svg>` document) for an icon hash. drawCharacter
 *  then renders it via Pixi v8's native `Graphics.svg(string)` parser —
 *  resolution-independent, no rasterization, no Image / Canvas hop. This
 *  path is preferred over the texture registry because it sidesteps the
 *  mobile-Safari rasterization failures that plagued the texture path. */
export function setCharacterSvgByIconHash(hash: string, svg: string): void {
  svgRegistry().set(hash, svg);
}

/** Drop all registered textures (called by GameStage cleanup between
 *  matches so a re-entry into the lobby doesn't keep stale textures). */
export function clearCharacterTextures(): void {
  registry().clear();
  svgRegistry().clear();
}

export type CharacterExpression = 'idle' | 'happy' | 'sad' | 'focused' | 'surprised';

export interface CharacterRenderOptions {
  expression?: CharacterExpression;
  label?: string;
  alpha?: number;
  facing?: -1 | 1;
}

export function hslHex(h: number, s: number, l: number): number {
  s /= 100; l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const r = Math.round(f(0) * 255);
  const g = Math.round(f(8) * 255);
  const b = Math.round(f(4) * 255);
  return (r << 16) | (g << 8) | b;
}

/** Palette = three-color override (body / accent / face-features).
 *  When `CharacterIcon.paletteId` is set, drawCharacter uses the
 *  palette colors instead of hue-derived ones. Adding a palette here +
 *  granting the id to a player's `inventory.palettes` is the whole
 *  feature surface — no per-game additions needed. */
export interface Palette {
  /** Body main fill (hsl). */
  body: { h: number; s: number; l: number };
  /** Accent / face highlights. */
  accent: { h: number; s: number; l: number };
  /** Face features (eyes, mouth) tint when expression supports it. */
  features: { h: number; s: number; l: number };
  /** Optional: animated body-color cycle. When true, body hue is a
   *  function of time; renderers must call `tickPalette()` per frame.
   *  Used by Mythic palettes only. */
  animated?: boolean;
}

export const PALETTES: Record<string, Palette> = {
  // Default palette — what every character starts with. Body hue is
  // taken from icon.hue; this entry is consulted only when a palette
  // id is set, so the "default" is the absence of paletteId.
  'palette-dusk': {
    body:     { h: 280, s: 70, l: 36 },
    accent:   { h: 340, s: 78, l: 62 },
    features: { h: 50,  s: 62, l: 72 },
  },
  'palette-bone': {
    body:     { h: 40,  s: 14, l: 86 },
    accent:   { h: 220, s: 12, l: 56 },
    features: { h: 220, s: 18, l: 28 },
  },
  'palette-emerald': {
    body:     { h: 150, s: 76, l: 38 },
    accent:   { h: 50,  s: 88, l: 60 },
    features: { h: 130, s: 32, l: 78 },
  },
  'palette-prismatic': {
    body:     { h: 0,   s: 80, l: 56 },   // base; cycles when animated
    accent:   { h: 50,  s: 88, l: 60 },
    features: { h: 240, s: 28, l: 18 },
    animated: true,
  },
  // Themed set palettes (Knight / Arcane / Royal / Naval) — the matching-set
  // glue for the armory bundles. Cloth outfits tint to `body`; armor trim uses
  // `accent`. KEEP IN SYNC with shell-3d cosmetics.ts + IconAvatar PALETTE_HSL.
  'palette-steel': {
    body:     { h: 216, s: 26, l: 45 },   // gunmetal steel-blue (cool enough to
    accent:   { h: 208, s: 30, l: 72 },   // resist the warm candle wash)
    features: { h: 220, s: 26, l: 24 },
  },
  'palette-arcane': {
    body:     { h: 262, s: 58, l: 40 },   // deep indigo-violet
    accent:   { h: 188, s: 82, l: 60 },   // luminous arcane cyan
    features: { h: 270, s: 38, l: 82 },
  },
  'palette-royal': {
    body:     { h: 278, s: 52, l: 32 },   // royal amethyst
    accent:   { h: 45,  s: 82, l: 56 },   // regal gold
    features: { h: 44,  s: 46, l: 86 },
  },
  'palette-naval': {
    body:     { h: 214, s: 52, l: 30 },   // deep navy
    accent:   { h: 42,  s: 72, l: 55 },   // brass / gold
    features: { h: 40,  s: 22, l: 88 },
  },
};

export function paletteColors(id: string | undefined, fallbackHue: number, fallbackAccentHue: number, t = 0): {
  body: number; shadow: number; accent: number; features: number;
} {
  const p = id ? PALETTES[id] : undefined;
  if (!p) {
    return {
      body: hslHex(fallbackHue, 72, 56),
      shadow: hslHex(fallbackHue, 72, 34),
      accent: hslHex(fallbackAccentHue, 78, 62),
      features: hslHex(fallbackAccentHue, 78, 72),
    };
  }
  // Prismatic / animated: cycle the body hue across time. `t` is in
  // seconds; one full cycle ~6s.
  const bodyH = p.animated ? (p.body.h + (t * 60)) % 360 : p.body.h;
  return {
    body: hslHex(bodyH, p.body.s, p.body.l),
    shadow: hslHex(bodyH, p.body.s, Math.max(8, p.body.l - 22)),
    accent: hslHex(p.accent.h, p.accent.s, p.accent.l),
    features: hslHex(p.features.h, p.features.s, p.features.l),
  };
}

export function drawCharacter(
  target: Container,
  icon: CharacterIcon | undefined,
  x: number,
  y: number,
  size: number,
  options: CharacterRenderOptions = {},
): Container {
  const c = new Container();
  c.x = x;
  c.y = y;
  c.alpha = options.alpha ?? 1;
  c.scale.x = options.facing ?? 1;
  target.addChild(c);

  const hashKey = iconHash(icon);

  // SVG-texture fast path: if the lobby's exact IconAvatar SVG was
  // pre-rasterized into a Pixi Texture (via the browser's SVG
  // renderer, which is far more capable than Pixi v8's built-in SVG
  // parser), render that Sprite so the in-game character matches the
  // lobby card pixel-for-pixel.
  const tex = registry().get(hashKey);
  if (tex) {
    const sp = new Sprite(tex);
    sp.anchor.set(0.5);
    sp.width = size;
    sp.height = size;
    c.addChild(sp);
    if (options.label) {
      const text = new Text({
        text: options.label,
        style: new TextStyle({
          fontFamily: 'Inter, sans-serif',
          fontSize: Math.max(9, size * 0.16),
          fill: 0xf8edd6,
          fontWeight: '700',
        }),
      });
      text.anchor.set(0.5, 0);
      text.y = size * 0.54;
      c.addChild(text);
    }
    return c;
  }

  const hue = icon?.hue ?? 22;
  const accentHue = icon?.accentHue ?? ((hue + 70) % 360);
  const bodyIdx = icon?.body ?? icon?.id ?? 0;
  const hatIdx = icon?.hat ?? 0;
  const faceIdx = icon?.face ?? icon?.id ?? 0;
  // Palette OVERRIDE — when icon.paletteId is set, fill colors come from
  // the palette table instead of the hue-derived defaults. Animated
  // palettes (Prismatic) cycle on Date.now() at draw time; the
  // CharacterSprite ticker triggers redraws so the cycle looks fluid.
  const palette = paletteColors(icon?.paletteId, hue, accentHue, (Date.now() % 360000) / 1000);
  const bodyColor = palette.body;
  const shadowColor = palette.shadow;
  const accentColor = palette.accent;
  const bg = new Graphics();
  bg.circle(0, 0, size * 0.5).fill({ color: hslHex(hue, 54, 14), alpha: 0.95 });
  bg.circle(-size * 0.12, -size * 0.16, size * 0.28).fill({ color: hslHex(hue, 70, 24), alpha: 0.45 });
  c.addChild(bg);

  const body = new Graphics();
  drawBody(body, bodyIdx, size, bodyColor, shadowColor);
  c.addChild(body);

  const face = new Graphics();
  drawFace(face, faceIdx, size, options.expression ?? 'idle', accentColor);
  c.addChild(face);

  if (hatIdx > 0) {
    const hat = new Graphics();
    drawHat(hat, hatIdx, size, accentColor);
    c.addChild(hat);
  }

  if (options.label) {
    const text = new Text({
      text: options.label,
      style: new TextStyle({
        fontFamily: 'Inter, sans-serif',
        fontSize: Math.max(9, size * 0.16),
        fill: 0xf8edd6,
        fontWeight: '700',
      }),
    });
    text.anchor.set(0.5, 0);
    text.y = size * 0.54;
    c.addChild(text);
  }

  return c;
}

export class CharacterSprite extends Container {
  private body = new Container();
  private idleT = Math.random() * Math.PI * 2;

  constructor(
    private readonly icon: CharacterIcon | undefined,
    private readonly size: number,
    private expression: CharacterExpression = 'idle',
    label?: string,
  ) {
    super();
    this.addChild(this.body);
    this.redraw(label);
  }

  setExpression(next: CharacterExpression, label?: string): void {
    this.expression = next;
    this.redraw(label);
  }

  tick(deltaMs: number): void {
    this.idleT += deltaMs * 0.004;
    this.body.y = Math.sin(this.idleT) * this.size * 0.025;
    this.body.scale.y = 1 + Math.sin(this.idleT + 0.8) * 0.015;
  }

  private redraw(label?: string): void {
    this.body.removeChildren();
    drawCharacter(this.body, this.icon, 0, 0, this.size, {
      expression: this.expression,
      ...(label !== undefined ? { label } : {}),
    });
  }
}

function drawBody(g: Graphics, idx: number, size: number, fill: number, shadow: number): void {
  const r = size * 0.5;
  // Archetype ids (≥100) get remapped to the same kind=idx%8 family the
  // lobby's IconAvatar uses (it falls back via BODY_SHAPES[idx % len]).
  // The dedicated Pixi archetype branches below are now unreachable —
  // kept as dead code in case we reintroduce them with matching lobby
  // SVG paths.
  if (idx >= 100) idx = idx - 100;
  if (false && idx === 100) {
    // Floating Orb: sphere body floating above ground, small shadow.
    g.ellipse(0, r * 0.55, r * 0.34, r * 0.08).fill({ color: 0x000000, alpha: 0.4 });
    g.circle(0, -r * 0.05, r * 0.42).fill({ color: fill });
    g.circle(-r * 0.18, -r * 0.22, r * 0.16).fill({ color: 0xffffff, alpha: 0.18 });
    g.circle(0, -r * 0.05, r * 0.42).stroke({ color: shadow, width: 1.5, alpha: 0.55 });
    return;
  }
  if (idx === 101) {
    // Eight-Legged: round body + 8 thin segmented legs.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      const lx = Math.cos(a) * r * 0.55;
      const ly = Math.sin(a) * r * 0.55;
      g.moveTo(0, 0).quadraticCurveTo(lx * 0.5, ly * 0.5 + r * 0.12, lx, ly).stroke({ color: shadow, width: Math.max(1.5, r * 0.06), cap: 'round' });
    }
    g.ellipse(0, 0, r * 0.36, r * 0.42).fill({ color: fill });
    // Two extra eye glints peeking from the body.
    g.circle(-r * 0.14, -r * 0.05, r * 0.04).fill({ color: 0xffffff, alpha: 0.7 });
    g.circle(r * 0.14, -r * 0.05, r * 0.04).fill({ color: 0xffffff, alpha: 0.7 });
    return;
  }
  if (idx === 102) {
    // Phantom: translucent sheet body, wavy bottom, drifts slightly.
    g.ellipse(0, -r * 0.05, r * 0.42, r * 0.5).fill({ color: fill, alpha: 0.55 });
    // Wavy bottom edge.
    g.moveTo(-r * 0.42, r * 0.45);
    for (let x = -0.4; x <= 0.4; x += 0.08) {
      const ph = Math.sin((x + 0.4) * Math.PI * 5) * 0.08;
      g.lineTo(x * r, (0.45 + ph) * r);
    }
    g.lineTo(r * 0.42, r * 0.45);
    g.stroke({ color: shadow, width: 1.5, alpha: 0.7 });
    g.ellipse(0, -r * 0.05, r * 0.42, r * 0.5).stroke({ color: 0xffffff, alpha: 0.25, width: 1 });
    return;
  }
  if (idx === 103) {
    // Living Crystal: 6-sided cluster, faceted, inner glow.
    const facets = 6;
    for (let i = 0; i < facets; i++) {
      const a0 = (i / facets) * Math.PI * 2 - Math.PI / 2;
      const a1 = ((i + 1) / facets) * Math.PI * 2 - Math.PI / 2;
      const x0 = Math.cos(a0) * r * 0.42;
      const y0 = Math.sin(a0) * r * 0.42;
      const x1 = Math.cos(a1) * r * 0.42;
      const y1 = Math.sin(a1) * r * 0.42;
      // Each facet a triangle from origin → its lighting varies.
      const facetL = 36 + (i * 6) % 28;
      g.poly([0, 0, x0, y0, x1, y1]).fill({ color: hslHex((i * 60) % 360, 38, facetL), alpha: 0.78 });
    }
    g.circle(0, 0, r * 0.18).fill({ color: 0xffffff, alpha: 0.35 });
    g.circle(0, 0, r * 0.42).stroke({ color: 0xffffff, width: 1.5, alpha: 0.45 });
    return;
  }
  if (idx === 104) {
    // Living Flame: flame-shaped body, two-tone gradient.
    g.moveTo(0, r * 0.5);
    g.bezierCurveTo(-r * 0.42, r * 0.4, -r * 0.45, -r * 0.05, -r * 0.18, -r * 0.32);
    g.bezierCurveTo(-r * 0.05, -r * 0.45, 0, -r * 0.55, 0, -r * 0.55);
    g.bezierCurveTo(0, -r * 0.55, r * 0.05, -r * 0.45, r * 0.18, -r * 0.32);
    g.bezierCurveTo(r * 0.45, -r * 0.05, r * 0.42, r * 0.4, 0, r * 0.5);
    g.fill({ color: fill });
    // Inner brighter core.
    g.moveTo(0, r * 0.32);
    g.bezierCurveTo(-r * 0.22, r * 0.22, -r * 0.22, -r * 0.05, -r * 0.08, -r * 0.18);
    g.bezierCurveTo(0, -r * 0.32, 0, -r * 0.32, 0, -r * 0.32);
    g.bezierCurveTo(0, -r * 0.32, 0, -r * 0.32, r * 0.08, -r * 0.18);
    g.bezierCurveTo(r * 0.22, -r * 0.05, r * 0.22, r * 0.22, 0, r * 0.32);
    g.fill({ color: 0xfff2c8, alpha: 0.55 });
    return;
  }
  if (idx === 105) {
    // Hexagon: hexagonal silhouette, beveled edges.
    const pts: number[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
      pts.push(Math.cos(a) * r * 0.46, Math.sin(a) * r * 0.46);
    }
    g.poly(pts).fill({ color: fill });
    // Inner bevel ring.
    const inner: number[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
      inner.push(Math.cos(a) * r * 0.34, Math.sin(a) * r * 0.34);
    }
    g.poly(inner).fill({ color: shadow, alpha: 0.4 });
    g.poly(pts).stroke({ color: 0xffffff, alpha: 0.3, width: 1 });
    return;
  }
  const kind = idx % 8;
  if (kind === 1) {
    g.roundRect(-r * 0.34, -r * 0.42, r * 0.68, r * 1.26, r * 0.24).fill({ color: fill });
    g.roundRect(-r * 0.34, r * 0.45, r * 0.68, r * 0.38, r * 0.16).fill({ color: shadow, alpha: 0.42 });
  } else if (kind === 2) {
    g.poly([-r * 0.38, -r * 0.32, -r * 0.24, -r * 0.68, -r * 0.06, -r * 0.36]).fill({ color: fill });
    g.poly([r * 0.38, -r * 0.32, r * 0.24, -r * 0.68, r * 0.06, -r * 0.36]).fill({ color: fill });
    g.ellipse(0, r * 0.1, r * 0.43, r * 0.66).fill({ color: fill });
    g.ellipse(0, r * 0.46, r * 0.34, r * 0.24).fill({ color: shadow, alpha: 0.34 });
  } else if (kind === 3) {
    g.circle(-r * 0.28, -r * 0.34, r * 0.18).fill({ color: fill });
    g.circle(r * 0.28, -r * 0.34, r * 0.18).fill({ color: fill });
    g.ellipse(0, r * 0.08, r * 0.48, r * 0.68).fill({ color: fill });
  } else if (kind === 4) {
    g.ellipse(0, r * 0.1, r * 0.48, r * 0.68).fill({ color: fill });
    g.rect(-r * 0.03, -r * 0.72, r * 0.06, r * 0.18).fill({ color: shadow });
    g.ellipse(-r * 0.18, r * 0.08, r * 0.12, r * 0.56).fill({ color: shadow, alpha: 0.2 });
    g.ellipse(r * 0.18, r * 0.08, r * 0.12, r * 0.56).fill({ color: shadow, alpha: 0.2 });
  } else if (kind === 5) {
    g.ellipse(0, -r * 0.02, r * 0.44, r * 0.58).fill({ color: fill });
    g.poly([-r * 0.44, r * 0.32, -r * 0.3, r * 0.62, -r * 0.14, r * 0.38, 0, r * 0.64, r * 0.14, r * 0.38, r * 0.3, r * 0.62, r * 0.44, r * 0.32]).fill({ color: fill });
  } else if (kind === 6) {
    g.roundRect(-r * 0.44, -r * 0.32, r * 0.88, r * 1.04, r * 0.1).fill({ color: fill });
    g.rect(-r * 0.36, r * 0.35, r * 0.72, r * 0.12).fill({ color: shadow, alpha: 0.32 });
  } else if (kind === 7) {
    g.ellipse(0, -r * 0.18, r * 0.54, r * 0.34).fill({ color: fill });
    g.roundRect(-r * 0.23, r * 0.08, r * 0.46, r * 0.58, r * 0.12).fill({ color: fill });
    g.ellipse(0, -r * 0.02, r * 0.18, r * 0.1).fill({ color: shadow, alpha: 0.26 });
  } else {
    g.ellipse(0, r * 0.06, r * 0.46, r * 0.66).fill({ color: fill });
    g.ellipse(-r * 0.16, -r * 0.1, r * 0.14, r * 0.22).fill({ color: 0xffffff, alpha: 0.12 });
    g.ellipse(0, r * 0.46, r * 0.34, r * 0.22).fill({ color: shadow, alpha: 0.28 });
  }
}

function drawFace(g: Graphics, idx: number, size: number, expression: CharacterExpression, accent: number): void {
  const r = size * 0.5;
  const eyeY = -r * 0.02;
  const eyeX = r * 0.18;
  const eyeR = r * ((idx % 8) === 7 ? 0.085 : 0.055);
  if ((idx % 8) === 7) {
    g.circle(0, eyeY, eyeR * 1.25).fill({ color: 0x0a0814 });
  } else if (expression === 'focused') {
    g.roundRect(-eyeX - eyeR, eyeY - eyeR * 0.2, eyeR * 2, eyeR * 0.55, eyeR * 0.2).fill({ color: 0x0a0814 });
    g.roundRect(eyeX - eyeR, eyeY - eyeR * 0.2, eyeR * 2, eyeR * 0.55, eyeR * 0.2).fill({ color: 0x0a0814 });
  } else {
    g.circle(-eyeX, eyeY, eyeR).fill({ color: 0x0a0814 });
    g.circle(eyeX, eyeY, eyeR).fill({ color: 0x0a0814 });
  }

  if (expression === 'happy') {
    g.arc(0, r * 0.16, r * 0.18, 0.12 * Math.PI, 0.88 * Math.PI).stroke({ color: 0x0a0814, width: Math.max(1, size * 0.025), cap: 'round' });
  } else if (expression === 'sad') {
    g.arc(0, r * 0.28, r * 0.16, 1.12 * Math.PI, 1.88 * Math.PI).stroke({ color: 0x0a0814, width: Math.max(1, size * 0.024), cap: 'round' });
  } else if (expression === 'surprised') {
    g.circle(0, r * 0.2, r * 0.065).fill({ color: 0x0a0814 });
  } else {
    g.moveTo(-r * 0.14, r * 0.18);
    g.quadraticCurveTo(0, r * 0.24, r * 0.14, r * 0.18);
    g.stroke({ color: 0x0a0814, width: Math.max(1, size * 0.024), cap: 'round' });
  }

  if ((idx % 3) === 0 || expression === 'happy') {
    g.circle(-r * 0.3, r * 0.16, r * 0.055).fill({ color: accent, alpha: 0.45 });
    g.circle(r * 0.3, r * 0.16, r * 0.055).fill({ color: accent, alpha: 0.45 });
  }
}

function drawHat(g: Graphics, idx: number, size: number, fill: number): void {
  const r = size * 0.5;
  const kind = idx % 8;
  if (kind === 1) {
    g.poly([0, -r * 0.82, -r * 0.36, -r * 0.28, r * 0.36, -r * 0.28]).fill({ color: fill });
    g.poly([-r * 0.22, -r * 0.42, 0, -r * 0.52, r * 0.18, -r * 0.38]).fill({ color: 0x0a0814, alpha: 0.42 });
  } else if (kind === 2) {
    g.poly([-r * 0.38, -r * 0.26, -r * 0.3, -r * 0.58, -r * 0.12, -r * 0.36, 0, -r * 0.62, r * 0.12, -r * 0.36, r * 0.3, -r * 0.58, r * 0.38, -r * 0.26]).fill({ color: fill });
    g.rect(-r * 0.4, -r * 0.28, r * 0.8, r * 0.08).fill({ color: 0x0a0814, alpha: 0.38 });
  } else if (kind === 3) {
    g.rect(-r * 0.28, -r * 0.72, r * 0.56, r * 0.36).fill({ color: fill });
    g.roundRect(-r * 0.46, -r * 0.38, r * 0.92, r * 0.12, r * 0.04).fill({ color: fill });
  } else if (kind === 4) {
    g.ellipse(0, -r * 0.34, r * 0.5, r * 0.3).fill({ color: fill });
    g.ellipse(0, -r * 0.2, r * 0.36, r * 0.12).fill({ color: 0x0a0814, alpha: 0.25 });
  } else if (kind === 5) {
    g.moveTo(-r * 0.28, -r * 0.36);
    g.lineTo(-r * 0.46, -r * 0.72);
    g.lineTo(-r * 0.34, -r * 0.52);
    g.lineTo(-r * 0.22, -r * 0.78);
    g.stroke({ color: fill, width: Math.max(2, size * 0.045), cap: 'round', join: 'round' });
    g.moveTo(r * 0.28, -r * 0.36);
    g.lineTo(r * 0.46, -r * 0.72);
    g.lineTo(r * 0.34, -r * 0.52);
    g.lineTo(r * 0.22, -r * 0.78);
    g.stroke({ color: fill, width: Math.max(2, size * 0.045), cap: 'round', join: 'round' });
  } else if (kind === 6) {
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      g.ellipse(Math.cos(a) * r * 0.12, -r * 0.58 + Math.sin(a) * r * 0.08, r * 0.11, r * 0.08).fill({ color: fill });
    }
    g.circle(0, -r * 0.58, r * 0.06).fill({ color: 0x0a0814, alpha: 0.5 });
  } else if (kind === 7) {
    g.roundRect(-r * 0.08, -r * 0.66, r * 0.16, r * 0.24, r * 0.02).fill({ color: fill });
    g.poly([0, -r * 0.68, -r * 0.07, -r * 0.82, r * 0.07, -r * 0.82]).fill({ color: 0xff8c42 });
  }
}
